/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import DatePickerOutline from 'components/MDDatePicker/DatePickerOutline';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  pr: 'ALL',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  branch: 'ALL',
  date_receive: Func.dateAdd(-1),
  date_send: Func.dateNow(),
};

function SearchInvite() {
  const mod = 'dashboard_stock';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [cboBranch, setCboBranch] = useState([]);
  const txtSearch = useRef(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);

    FetchData('POST', `/${mod}/viewinvite`, app.token, {
      lang: app.lang,
      search: data.search,
      ref_id: id,
      branch: data.branch,
      date_receive: data.date_receive,
      date_send: data.date_send,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
      }
      setLoading(false);
    });
  };

  const init = () => {
    FetchData('POST', `/${mod}/initinvite`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const open = () => {
    setModalAdd(true);
    view(find);

    setTimeout(function () {
      txtSearch.current.focus();
    }, 500);
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value })}
            >
              <MenuItem value='ALL'>สาขา</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <DatePickerOutline
              onChange={([date]) => {
                view({ ...find, date_receive: Func.dateFormat(date) });
              }}
              value={new Date(find.date_receive)}
              label='วันที่ขอ'
              input={{
                variant: 'outlined',
                fullWidth: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <DatePickerOutline
              onChange={([date]) => {
                view({ ...find, date_send: Func.dateFormat(date) });
              }}
              value={new Date(find.date_send)}
              label='วันที่ให้'
              input={{
                variant: 'outlined',
                fullWidth: true,
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 550 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' width={40}>
                      {t(`attr.image`)}
                    </TableCell>
                    <TableCell align='left' className='nowrap' width={10}>
                      สาขา
                    </TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align='right' width={150}>
                      ขอ
                    </TableCell>
                    <TableCell align='right' width={120}>
                      ให้
                    </TableCell>
                    <TableCell align='right' width={120}>
                      ขอซื้อ
                    </TableCell>
                    <TableCell align='right' width={120}>
                      รอเบิก
                    </TableCell>
                    <TableCell align='right' width={120}>
                      คลัง
                    </TableCell>
                    <TableCell align='right' width={120}>
                      บิล
                    </TableCell>
                    <TableCell align='center' width={120}>
                      ขอโอน/ใช้งาน
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>
                        <Tooltip title={row.branch_use_full}>
                          <MDBox className='nowrap'>{row.branch_use}</MDBox>
                        </Tooltip>
                        <Tooltip title='ผู้ขอ'>
                          <MDBox className='nowrap'>{row.user_name}</MDBox>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.product_code}</MDBox>
                        <MDBox className='nowrap'>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-primary'>
                          {Func.numberDisplay(row.quantity_balance)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-warning'>
                          {Func.numberDisplay(row.quantity_send)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-info'>
                          {Func.numberDisplay(row.quantity_purchase)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-danger'>
                          {Func.numberDisplay(row.quantity_ready)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-success'>
                          {Func.numberDisplay(row.quantity_stock)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.count_item)}</TableCell>
                      <TableCell align='center'>
                        <Tooltip title='ขอโอน'>
                          <MDBox className='nowrap'>{Func.dateDisplay(row.date_record)}</MDBox>
                        </Tooltip>
                        <Tooltip title='ใช้งาน'>
                          <MDBox className='nowrap color-danger'>
                            {Func.dateDisplay(row.date_use)}
                          </MDBox>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalAdd(false)}
      >
        <DialogTitle>
          รายการขอสินค้า
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <div>
      <MDButton
        size='small'
        sx={{
          fontSize: '0.875rem',
          display: 'inline-block',
          textAlign: 'left',
          minHeight: 'auto',
          p: 0,
          m: 0,
          fontWeight: 'normal',
        }}
        onClick={open}
      >
        แสดงรายละเอียด <Icon color='primary'>launch</Icon>
      </MDButton>

      {renderAdd()}
      {renderLoading()}
      {notify}
    </div>
  );
}

export default SearchInvite;
